import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Footer from '../../components/Footer';
import 'wicg-inert';
import ScrollToTop from "../../libs/scrollToTop";
import { useKonami } from 'react-konami-code';
import Nav from '../../components/Nav.js';
import SecondaryNav from '../../components/SecondaryNav';

import heroMobileImage from '../../images/articles/wf-isf-economic-advantage-of-issuing-green-bonds_767x577.jpg';
import heroDesktopImage from '../../images/articles/wf-isf-economic-advantage-of-issuing-green-bonds_1164x584.jpg';

import ModalOverlay from "../../components/ModalOverlay";

import { fireTag } from '../../libs/analytics.js';

const Bonds = ({
				   handleEvent,
				   modalOverlay,
				   closeExitOverlay,
				   addExitClickListeners,
			   }) => {

	const [enableScrollGA, setEnableScrollGA] = useState(false);
	const [shouldBeInert, setShouldBeInert] = useState(false);
	const [gridVisible, setGridVisible] = useState(false);
	const heroOGImage = 'wf-isf-economic-advantage-of-issuing-green-bonds_ogimage.jpg';

	addExitClickListeners();

	const toggleInert = () => {
		setShouldBeInert(!shouldBeInert)
	}


	const toggleGrid = () => {
		const grid = document.getElementsByClassName('grid')[0];
		grid.style.display = (!gridVisible) ? 'none' : 'block';
		setGridVisible(!gridVisible)
	}

	const KonamiElement = () => {
		useKonami(toggleGrid, {
			code: [37, 39, 37, 39]
		});
		return <div className="grid" style={{display: gridVisible ? 'block' : 'none'}}/>;
	};

	const linkDisclosures = () => {
		const bodyDisclosures = document.querySelectorAll('.body-disclosures');
		const footerDisclosures = document.querySelectorAll('.footer-disclosures');
		bodyDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = footerDisclosures[disclosureNumber - 1];
				fireTag('A2', disclosureNumber);
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});

				window.setTimeout(() => {
					// console.log(' ======== ');
					// console.log(targetElement);
					targetElement.focus();
				}, 1000);

			});
		});

		footerDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = bodyDisclosures[disclosureNumber - 1];
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});
	}


	useEffect(() => {

		// console.log(`To see grid overlay, type ↑ ↑ ↓ ↓ ← → ← → B A inside the browser window`);
		ScrollToTop(setEnableScrollGA)
		linkDisclosures();

	}, []); // empty array says only use this effect once

	let mastheadImage;
	let bodyImage;

	switch (true) {
		case (window.innerWidth < 768):
			mastheadImage = heroMobileImage;
			break;
		case (window.innerWidth < 1440):
			mastheadImage = heroDesktopImage;
			break;
		case (window.innerWidth >= 1440):
			mastheadImage = heroDesktopImage;
			break;
	}
	// console.log(mastheadImage);
	// console.log(process.env.PUBLIC_URL);

	return (
		<>
			<Helmet>
				<title>Analyzing the economic advantage of issuing green bonds</title>
				<meta
					name="description"
					content="Can corporations realize a cost-of-capital benefit by issuing bonds with projected environmental benefits?"
				/>
				<meta property="og:title" content="Analyzing the economic advantage of issuing green bonds"/>
				<meta
					property="og:description"
					content="Can corporations realize a cost-of-capital benefit by issuing bonds with projected environmental benefits?"
				/>
				<meta property="og:image" content="https://welcome.wf.com/impact/images/articles/wf-isf-economic-advantage-of-issuing-green-bonds_ogimage.jpg" />
				<meta property="og:url" content={`https://welcome.wf.com/impact/sustainability/economic-advantage-of-issuing-green-bonds/`}/>
				<meta property="twitter:title" content="Analyzing the economic advantage of issuing green bonds"/>
				<meta
					property="twitter:description"
					content="Can corporations realize a cost-of-capital benefit by issuing bonds with projected environmental benefits?"
				/>
			</Helmet>
			{/*TODO: isMobile should be passed in from route, not calc'd here */}
			<Nav handleEvent={handleEvent} isNotHomePage={false} activePage="articles" isMobile={window.innerWidth < 768} showExternalNav toggleInert={toggleInert} shouldBeInert={shouldBeInert}/>
			{!window.innerWidth < 768 && (
				<SecondaryNav page="Articles" shouldBeInert={shouldBeInert} activePage="articles"/>
			)}

			<main className='flex-container'>
				<div className="flex-row">
					<div className="flex-col-sm-12 flex-col-md-8 text-center headline">
						<h1 id="articles-main-h1" style={{marginBottom: 10}}>
							Analyzing the economic advantage of issuing green bonds
						</h1>
						<h2 className="summary">
							Can corporations realize a cost-of-capital benefit by issuing bonds with projected environmental benefits?
						</h2>
					</div>

				</div>


				<div className="flex-row full-width-md">
					<div className="flex-col-lg-10 flex-col-md-10 text-center hero-container">
						<img src={mastheadImage} alt="Three coworkers huddle around a computer in an office."/>
					</div>
				</div>


				<div className="flex-row">
					<div className="flex-col-md-8 flex-col-sm-12 text-center article-intro info-graphic">
						<div className="article-info">
							<div>
								<p>February 21, 2024</p>
							</div>
							<div>
								<p>2-minute read</p>
							</div>
						</div>
						<p>
							When corporations seek to raise capital, they may choose to issue green bonds. Green bonds are like typical corporate bonds but include a commitment to using the capital raised towards projects with intended environmental benefits.
						</p>
						<p>
							There are various reasons why corporations may pursue green bonds. A primary motivation is that by highlighting an investment of hundreds of millions of dollars toward efforts like developing electric vehicles or advancing recycling technology, they send a strong message to their customers, investors, regulators, and employees about their position on environmental issues and the fundamental direction of their business.
						</p>
						<p>
							However, beyond the signaling benefits, are there any financial benefits to issue a bond formally labeled as “green”? Does the growing amount of assets under management from investors dedicated to green or environmental activity translate to lower borrowing costs for corporations issuing green bonds?
						</p>
						<p>
							A study developed in July 2023 by Wells Fargo’s Corporate & Investment Banking (CIB) team examines data from January 2021 to June 2023 and explores these questions and the existence of a “greenium” — a fusion of <i>green</i> and <i>premium</i> — to see if the market assigns a price premium, indicating a lower borrowing cost, to green bonds when compared to conventional securities issued by the same firm.
						</p>
						<p>
							In the study, CIB’s Sustainable Finance Debt Capital Markets team found that green bonds have traded in a 2 – 5 basis points (bps) range inside conventional comparable securities on a median basis since 2021, denoting a potential funding advantage in both U.S. and European markets.
						</p>
					</div>
				</div>


				<div className="flex-row full-width">
					<div className="flex-row text-center">
						<div className="flex-col-md-8 flex-col-sm-12 text-center content-box">
							<div className="content-header bulleted">
								<h2 className="content-title bulleted">Key insights from the Wells Fargo Corporate & Investment Banking analysis</h2>
							</div>
							<div className="inner-content bulleted">
								<div className="content">
									<div>
										<ul>
											<li className="offset-title">Observed median 2-5 bps secondary trading advantage for labeled green bonds compared to conventional counterparts.</li>
											<li className="offset-title">Market statistics on new bond issuances show more demand for green-labeled bonds than comparable conventional bonds.</li>
											<li className="offset-title">In the secondary market, the observed greenium peaked in 2021, tapered through 2022, then expanded back toward longer-term medians in the first half of 2023.</li>
										</ul>
									</div>
								</div>

							</div>
						</div>
					</div>
				</div>

				<div className="flex-row">

					<div className="flex-col-md-8 flex-col-sm-12 text-center article-intro info-graphic">
						<p>
							Read the full July 2023 report: <a onClick={() => {fireTag('A1', 'exploring evidence of a “greenium” in ig usd & eur corporate secondary markets')}} target="_blank" href="../../docs/Exploring Evidence of the Greenium.pdf" rel="noreferrer" data-click-listener="false">Exploring Evidence of a “Greenium” in IG USD & EUR Corporate Secondary Markets</a>.
						</p>
					</div>
				</div>


				{/*<KonamiElement className="grid"/>*/}
			</main>

			<Footer id="footer" footerStyles="global" shouldBeInert={shouldBeInert} activePage="sustainability" enableScrollGA={enableScrollGA}/>
			{/* Adobe retargeting tag */
			}
			{
				/*
					REMOVE TAG
						<img alt="" src="https://rtd-tm.everesttech.net/upi/?sid=s6cvr4h2qH04B3WUwCOG&cs=1" style={{display: 'none'}}/>
				*/
			}
			{
				modalOverlay && modalOverlay.open && (
					<ModalOverlay closeExitOverlay={closeExitOverlay} modalOverlay={modalOverlay}/>
				)
			}
		</>
	);
};

export default withRouter(Bonds);
